import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Eye, Edit, Trash2, Calendar, Paperclip } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function AtestadoCard({ atestado, onEditar, onVisualizar, onExcluir }) {
  const formatCurrency = (value) => {
    if (!value) return null;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return null;
    }
  };

  return (
    <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-slate-900 mb-1">
                  {atestado.instituicao}
                </h3>
                {atestado.resumo_executivo && (
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {atestado.resumo_executivo}
                  </p>
                )}
              </div>
            </div>

            {atestado.tipos && atestado.tipos.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-1">Tipos:</p>
                <div className="flex flex-wrap gap-1">
                  {atestado.tipos.map((tipo, idx) => (
                    <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {tipo}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {atestado.competencias_tecnicas && atestado.competencias_tecnicas.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-1">Competências:</p>
                <div className="flex flex-wrap gap-1">
                  {atestado.competencias_tecnicas.slice(0, 8).map((comp, idx) => (
                    <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {comp}
                    </Badge>
                  ))}
                  {atestado.competencias_tecnicas.length > 8 && (
                    <Badge variant="outline" className="bg-slate-50 text-slate-600">
                      +{atestado.competencias_tecnicas.length - 8}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-3">
              {atestado.data_emissao && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Emissão: {formatDate(atestado.data_emissao)}</span>
                </div>
              )}
              {atestado.data_inicio && atestado.data_fim && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Período: {formatDate(atestado.data_inicio)} a {formatDate(atestado.data_fim)}</span>
                </div>
              )}
              {atestado.valor_contrato && (
                <div className="flex items-center gap-1">
                  <span className="font-semibold text-green-700">
                    {formatCurrency(atestado.valor_contrato)}
                  </span>
                </div>
              )}
              {atestado.anexo_url && (
                <div className="flex items-center gap-1">
                  <Paperclip className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-700">Anexo</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:w-48">
            <Button
              variant="outline"
              onClick={() => onVisualizar(atestado)}
              className="w-full"
            >
              <Eye className="w-4 h-4 mr-2" />
              Visualizar
            </Button>
            <Button
              variant="outline"
              onClick={() => onEditar(atestado)}
              className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
            <Button
              variant="outline"
              onClick={() => onExcluir(atestado)}
              className="w-full border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}